import React, { useEffect, useRef, useState } from 'react';
import Rodal from 'rodal';
import { Form } from 'informed';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InputDate, InputDateTime, InputTypeText } from '../../../elements';
import { validateConvocationStart } from './convocationPeriod';
import { buildConvocationMessage } from './convocationMessage';
import { getConvocationModalState } from './convocationModalState';
import { IApplication, IProcess } from '../types';

export type ConvocationPayload = {
  message: string,
  start_at: string,
  notice_item: string,
  notice_url: string,
  result_publication_date: string,
};

type Props = {
  show: boolean,
  submitting: boolean,
  targetApplications: IApplication[],
  process?: IProcess,
  reachLabel: string,
  isResend: boolean,
  onClose: () => void,
  onConfirm: (payload: ConvocationPayload) => void,
};

const ConvocationStepperModal = ({
  show,
  submitting,
  targetApplications,
  process,
  reachLabel,
  isResend,
  onClose,
  onConfirm,
}: Props) => {
  const { initialStep } = getConvocationModalState(isResend);
  const [step, setStep] = useState<1 | 2>(initialStep);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const formApiRef = useRef<any>(null);
  // Último texto padrão gerado: enquanto a mensagem for igual a ele, a Secretaria não
  // editou nada e o texto pode acompanhar as mudanças do passo 1.
  const generatedRef = useRef('');

  const processStart = (process && process.start_date) || '';
  const processEnd = (process && process.end_date) || '';

  // Só existe uma convocação: o que está salvo no processo é o estado dela.
  const savedValues = () => ({
    start_at: (process && process.convocation_start_at) || '',
    notice_item: (process && process.notice_item) || '',
    notice_url: (process && process.notice_url) || '',
    result_publication_date: (process && process.result_publication_date) || '',
  });

  useEffect(() => {
    if (!show) return;

    setStep(initialStep);
    setError('');
    generatedRef.current = '';

    // O reenvio repete a mensagem que os demais candidatos receberam.
    if (isResend) {
      setMessage((process && process.convocation_email_message) || '');

      return;
    }

    setMessage('');

    if (formApiRef.current) {
      formApiRef.current.setValues(savedValues());
    }
  }, [show, isResend, initialStep, process]); // eslint-disable-line react-hooks/exhaustive-deps

  const values = () => {
    const api = formApiRef.current;

    return {
      start_at: (api && api.getValue('start_at')) || '',
      notice_item: (api && api.getValue('notice_item')) || '',
      notice_url: ((api && api.getValue('notice_url')) || '').trim(),
      result_publication_date: (api && api.getValue('result_publication_date')) || '',
    };
  };

  const goToEmail = () => {
    const data = values();
    const startError = validateConvocationStart({
      startAt: data.start_at,
      processStart,
      processEnd,
    });

    if (startError) return setError(startError);
    if (!data.notice_item.trim()) return setError('Informe o item do Edital.');
    if (!/^https?:\/\/\S+\.\S+$/i.test(data.notice_url)) {
      return setError('Informe o link do Edital, começando com https://.');
    }
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(data.result_publication_date)) {
      return setError('Informe a data de divulgação do resultado.');
    }

    setError('');

    // A mensagem abre com o texto padrão montado a partir dos dados do passo 1. Se a
    // Secretaria voltar e mudar um dado sem ter editado o texto, ele é refeito; depois de
    // editado, o que ela escreveu é mantido.
    const generated = buildConvocationMessage({
      courseName: (process && process.course.name) || '',
      termLabel: (process && process.term_label) || '',
      noticeItem: data.notice_item.trim(),
      noticeUrl: data.notice_url,
      startAt: data.start_at,
      processEnd,
      resultPublicationDate: data.result_publication_date,
    });

    if (!message.trim() || message === generatedRef.current) {
      setMessage(generated);
    }

    generatedRef.current = generated;
    setStep(2);
  };

  const confirm = () => {
    if (!message.trim()) return setError('A mensagem é obrigatória.');

    setError('');
    // No reenvio o passo 1 não é renderizado: os dados do edital vêm do processo.
    onConfirm({ message, ...(isResend ? savedValues() : values()) });
  };

  return (
    <Rodal
      closeOnEsc
      width={680}
      height={isResend ? 620 : 760}
      visible={show}
      onClose={onClose}
      className="convocation-modal"
      customStyles={{ padding: 0, borderRadius: 18, overflow: 'hidden' }}
    >
      <div className="cvm-container">
        <div className="cvm-header">
          <span className="cvm-kicker">{isResend ? 'Notificação' : 'Convocação'}</span>
          <h2>{isResend ? 'Reenviar e-mail' : 'Nova convocação de candidatos'}</h2>
          {!isResend && (
            <div className="cvm-stepper">
              <div className={`cvm-step ${step === 1 ? 'is-active' : 'is-done'}`}>
                <span className="cvm-step-dot">{step > 1 ? <FontAwesomeIcon icon="check" /> : 1}</span>
                <span className="cvm-step-label">Dados da convocação</span>
              </div>
              <div className={`cvm-step-line ${step > 1 ? 'is-done' : ''}`} />
              <div className={`cvm-step ${step === 2 ? 'is-active' : ''}`}>
                <span className="cvm-step-dot">2</span>
                <span className="cvm-step-label">Revisão e envio</span>
              </div>
            </div>
          )}
        </div>
        <div className="cvm-body">
          {/* O formulário fica sempre montado (só troca a visibilidade) para não perder
              os valores ao alternar entre os passos. */}
          {!isResend && (
            <div className="cvm-form" style={{ display: step === 1 ? 'block' : 'none' }}>
              <div className="cvm-banner">
                <FontAwesomeIcon icon="circle-info" />
                <span>
                  {reachLabel}
                  <br />
                  Prazo final para envio: <b>{processEnd}</b> (data final do processo, não é editável).
                </span>
              </div>
              <Form getApi={(api: any) => { formApiRef.current = api; }}>
                <InputDateTime field="start_at" label="Início do envio" />
                <small className="cvm-hint">Entre {processStart} e {processEnd}.</small>
                <div className="row">
                  <div className="col-6">
                    <InputTypeText field="notice_item" label="Item do Edital" />
                  </div>
                  <div className="col-6">
                    <InputDate field="result_publication_date" label="Divulgação do resultado" />
                  </div>
                </div>
                <small className="cvm-hint">
                  Citados no e-mail de convocação e no de aprovação de documentos.
                </small>
                <InputTypeText field="notice_url" label="Link do Edital" />
                <small className="cvm-hint">
                  Endereço completo do Edital de Inscrição, citado no e-mail de convocação.
                </small>
              </Form>
            </div>
          )}
          <div style={{ display: step === 2 ? 'block' : 'none' }}>
            <p className="cvm-summary">
              <b>{targetApplications.length} candidato(s)</b> receberão o e-mail com o link individual
              para envio dos documentos.
            </p>
            <div className="cvm-candidates">
              {targetApplications.map(application => (
                <div key={application.id} className="cvm-candidate">
                  <div className="cvm-candidate-info">
                    <strong>{application.assumed_name || application.real_name}</strong>
                    <span>{application.email}</span>
                  </div>
                  {application.classification != null && (
                    <span className="tcp-rank">{application.classification}º lugar</span>
                  )}
                </div>
              ))}
            </div>
            <label className="cvm-field" htmlFor="convocation-message">
              <span>Mensagem do e-mail</span>
              <textarea
                id="convocation-message"
                rows={14}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </label>
            <small className="cvm-hint">
              {isResend
                ? 'Mesma mensagem enviada na convocação. Pode ser editada antes do reenvio.'
                : 'Texto padrão preenchido com os dados da convocação. Pode ser editado antes do envio.'}
              {' '}O link de acesso de cada candidato e a assinatura entram automaticamente no final.
            </small>
          </div>
        </div>
        {error && <small className="cvm-error">{error}</small>}
        <div className="cvm-footer">
          <button className="btn btn-black" type="button" onClick={onClose}>Cancelar</button>
          <div className="cvm-footer-right">
            {step === 2 && !isResend && (
              <button className="btn btn-black" type="button" onClick={() => setStep(1)}>Voltar</button>
            )}
            {step === 1 ? (
              <button className="btn btn-primary" type="button" onClick={goToEmail}>Avançar</button>
            ) : (
              <button className="btn btn-primary" type="button" disabled={submitting} onClick={confirm}>
                <FontAwesomeIcon icon="paper-plane" />
                {submitting ? 'Enviando...' : (isResend ? 'Enviar' : 'Confirmar e enviar')}
              </button>
            )}
          </div>
        </div>
      </div>
    </Rodal>
  );
};

export default ConvocationStepperModal;
